import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { db } from "@/firebase";
import { getDocs, collection, addDoc, setDoc, doc, query, where, limit, getDoc, updateDoc } from "firebase/firestore";
import { addLoadingTask } from "@/store/uiSlice";
import { RootState } from "@/store/store";
import { AnonymousUser, User } from "@/types";
import { pick } from "lodash";
import { sanitizePhone } from "@/utils/phones";

export const orgCustomersApi = createApi({
  reducerPath: "orgCustomers",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["orgCustomers"],
  endpoints: (builder) => ({
    fetchOrgUsers: builder.query({
      queryFn: async (organizationId: string, { dispatch }) => {
        const TASK = "Usuarios de la organización";

        if (!organizationId) {
          return { error: {
            status: 400,
            message: "Consulta inválida"
          } };
        }

        try {
          dispatch(addLoadingTask(TASK));
          const collectionRef = collection(db, `organizations/${organizationId}/customers`);

          const response = await getDocs(collectionRef).then((querySnapshot) => {
            return querySnapshot.docs.reduce((acc, doc: any) => {
              return [...acc, { ...doc.data(), id: doc.id }];
            }, [] as Partial<User>[]);
          });

          return { data: response };  
        } catch (error) {
          return { error };
        } 
      },
      providesTags: ["orgCustomers"],
    }),
    addCustomer: builder.mutation({
      queryFn: async (
        {
          organizationId,
          draft,
        }: {
          organizationId: string;
          draft: AnonymousUser;
        },
        { getState }
      ) => {
        try {
          const { auth } = getState() as RootState;
          const createdBy = auth?.user?.uid ?? null;

          const ALLOWED_FIELDS = ["email", "name", "sanitizedName", "sanitizedLastname", "lastname", "phone", "locations"];
          debugger
          // --- 1. Preparar payload ---
          const payload: Partial<AnonymousUser> = {
            ...pick(draft, ALLOWED_FIELDS),
            phone: sanitizePhone(draft.phone ?? ""),
            sanitizedName: draft.name?.toLowerCase(),
            sanitizedLastname: draft?.lastname?.toLowerCase(),
            createdBy,
            createdAt: Date.now(),
            locations: draft?.locations ?? [],
            email: draft?.email ?? '',
         };

          const cleanedPayload = Object.fromEntries(
            Object.entries(payload).filter(([_, v]) => v !== undefined)
          );

          // --- 2. Crear usuario global ---
          const userRef = collection(db, "users");
          const userQuery = query(userRef, where('phone', '==', cleanedPayload.phone), limit(1));

          let draftUser = null;

          const availableUsers = await getDocs(userQuery);

          if (availableUsers.empty) {
            const newUserRef = await addDoc(userRef, cleanedPayload);
            draftUser = { ...cleanedPayload, id: newUserRef.id  };
          } else {
            // use the first matching user
            const firstDoc = availableUsers.docs[0];
            draftUser = {
              ...firstDoc.data(),
              id: firstDoc.id,
            };
          }

          // --- 3. Crear copia dentro de organización ---

          if (!draftUser) {
            throw new Error();
          }

          const customerRef = doc(
            db,
            `organizations/${organizationId}/customers/${draftUser.id}`
          );


          const availableCustomer = await getDoc(customerRef);

          if (availableCustomer.exists()) {
            return { 
              error: { 
                status: 409,
                message: 'El usuario ya existe en la organización',
              }
            };
          } else {
            await setDoc(customerRef, cleanedPayload);
            return {
              data: {
                ...cleanedPayload,
                 id: draftUser.id,
              }
            }
          }
        } catch  {
           return {
            error: {
              status: 500,
              message: 'Error al crear usuario',
            }
          }
        }
      },
      invalidatesTags: ["orgCustomers"],
    }),
    getCustomerByPhone: builder.query({
      queryFn: async (
        { organizationId, phone }: { organizationId: string; phone: string },
        { dispatch }
      ) => {
        const TASK = "Buscar usuario por teléfono";

        if (!organizationId || !phone) {
          return { error: { message: "organizationId y phone son requeridos" } };
        }

        dispatch(addLoadingTask(TASK));

        try {
          const sanitized = sanitizePhone(phone);

          if (!sanitized) {
            return { error: { message: "Número de teléfono inválido" } };
          }

          // Buscar en clientes de la organización
          const orgRef = collection(db, `organizations/${organizationId}/customers`);
          const orgQuery = query(orgRef, where("phone", "==", sanitized), limit(5));
          let snapshot = await getDocs(orgQuery);

          // Si no existe en organización, buscar en users global
          if (snapshot.empty) {
            const usersRef = collection(db, "users");
            const userQuery = query(usersRef, where("phone", "==", sanitized), limit(5));
            snapshot = await getDocs(userQuery);
          }
    
          if (snapshot.empty) {
            return { error: {
              message: "Cliente no encontrado",
              status: 404,
            } }; 
          }

          const results = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
          return { data: results };

        } catch (error) {
          console.error("Error al buscar cliente:", error);
          return {
            error: {
              message: "Error en la consulta de base de datos",
              status: 500,
            },
          };
        }
      },
    }),
    updateCustomer: builder.mutation<
      Partial<User>,
      { organizationId: string; payload: Partial<AnonymousUser | User> & { id: string } }
    >({
      queryFn: async ({ organizationId, payload }, { getState }) => {
        try {
          const store = (getState as () => any)();

          if (!organizationId || !payload?.id) {
            return {
              error: {
                status: 400,
                message: "organizationId e id del usuario son requeridos",
              },
            };
          }

         

          const customerRef = doc(
            db,
            `organizations/${organizationId}/customers/${payload.id}`
          );

          const customerSnap = await getDoc(customerRef);


          const isNew = !customerSnap.exists();

        let draft;

        if (isNew) {
          // CREATE
          draft = {
            createdBy: store?.auth?.user?.uid,
            createdAt: Date.now(),
            name: payload.name,
            sanitizedName: payload.name?.toLowerCase(),
            sanitizedLastname: payload.lastname?.toLowerCase(),
            lastname: payload.lastname,
            phone: sanitizePhone(payload.phone ?? ""),
            locations: payload?.locations || [],
          };

          await setDoc(customerRef, draft);
        } else {
          // UPDATE
          draft = {
            ...pick(payload, ["locations", "phones"]),
            lastUpdatedBy: store?.auth?.user?.uid,
            lastUpdatedAt: Date.now(),
          };

          await updateDoc(customerRef, draft);  
        }   

          return {
            data: {
              ...draft,
              id: payload.id,
            },
          }

        } catch (error) {
          console.error( error);
          return {
            error: {
              status: 500,
              message: "Error al crear o actualizar el usuario",
            },
          };
        }
      },
      invalidatesTags: ["orgCustomers"],
    }),


  }),
});
             

export const { useFetchOrgUsersQuery, useAddCustomerMutation, useGetCustomerByPhoneQuery, useLazyGetCustomerByPhoneQuery, useUpdateCustomerMutation } = orgCustomersApi;